import { LDContext } from "launchdarkly-react-client-sdk";
import { UserAuthClientResponse } from "recoil/types";

/**
 * Custom function to get sanitized user data from recoil state.
 *
 * **********************************************************************************************
 * IMPORTANT: DO NOT put PHI in here! This data is sent directly to multiple 3rd-party services.
 * **********************************************************************************************
 */
export const getSanitizedUserData = (patientInfo: UserAuthClientResponse) => {
  const auth = patientInfo.auth0;
  const { user } = patientInfo;

  return {
    last_login: auth.last_login,
    is_verified_email: String(user.is_verified_email),
    date_joined: user?.date_joined || "",
    consent_status: user?.consent_status || "",
    survey_status: user?.survey_status || "",
    has_aneuploidy_consent: String(user?.has_aneuploidy_consent),
    has_carrier_consent: String(user?.has_carrier_consent),
  };
};

/**
 * Build the LaunchDarkly context for an authenticated patient. lims_id is the
 * targeting attribute for lims-gated flags (e.g. the Spanish toggle) — the
 * backend targets the same — so it must be present for the flag to turn on for
 * authenticated patients. It's added directly here rather than via
 * getSanitizedUserData so it isn't also broadcast to analytics.
 */
export const buildPatientLdContext = (
  patientInfo: UserAuthClientResponse,
): LDContext => ({
  kind: "user",
  key: patientInfo.auth0.user_id,
  ...getSanitizedUserData(patientInfo),
  lims_id: patientInfo.lims_id || "",
});

/**
 * Custom function to get detailed user data.
 * To be used when we need data that is not cheap to access. Expectation is to pass specific data types you want.
 */
export const getDetailedUserData = (
  patientInfo?: UserAuthClientResponse,
  dataType?: string,
) => {
  const requisitions = patientInfo?.requisitions;
  const mostRecentRequisition = requisitions?.[0];

  const baseData = {
    lims_id: patientInfo?.lims_id || "",
  };

  if (dataType === "most_recent_requisition") {
    return {
      ...baseData,
      most_recent_requisition: mostRecentRequisition,
    };
  }

  return baseData;
};
